import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ChangeEvent, useCallback, useMemo } from "react";
import { WEEKS } from "src/constants/weeks";
import { usePrescription } from "src/context/PrescriptionProvider";
import { useSelectableDosages } from "src/hooks/useSelectableDosages";

interface Props {
  medicationName: string;
  template: string;
  index: number;
  instruction: {
    week?: string;
    dosage: string;
    interval: string;
    interval_count: number;
    amount: string;
  };
  setDosages: React.Dispatch<React.SetStateAction<string[]>>;
}

const DosageInstruction = ({
  index,
  instruction,
  setDosages,
  template,
}: Props) => {
  const productId = usePrescription((p) => p.product_id);
  const selectableDosages = useSelectableDosages(productId, "injectable");

  const value = useMemo(() => {
    return template
      .replace("{{week}}", instruction.week ?? "")
      .replace("{{amount}}", instruction.amount)
      .replace("{{dosage}}", instruction.dosage)
      .replace(
        "{{duration}}",
        `${instruction.interval_count} ${instruction.interval}s`
      );
  }, [
    instruction.amount,
    instruction.dosage,
    instruction.interval,
    instruction.interval_count,
    instruction.week,
    template,
  ]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDosages((dosages) => [
        ...dosages.slice(0, index),
        event.target.value,
        ...dosages.slice(index + 1),
      ]);
    },
    [index, setDosages]
  );

  return (
    <Stack gap={2}>
      <Typography>{WEEKS[index]}</Typography>
      <TextField
        select
        value={instruction.dosage ?? ""}
        label="Dosage"
        onChange={handleChange}
      >
        {selectableDosages.map((dosage) => (
          <MenuItem key={dosage} value={dosage}>
            {dosage}
          </MenuItem>
        ))}
      </TextField>
      <TextField multiline minRows={2} value={value} disabled />
    </Stack>
  );
};

export default DosageInstruction;
